/**
 * /trouver — « Trouvez ma voiture » (2026-10-12, formulaire minimal le 2026-10-15).
 *
 * UN SEUL ÉCRAN, À FINIR EN MOINS D'UNE MINUTE. Deux questions obligatoires —
 * le budget et le type de voiture — que tout acheteur sait dire en une
 * seconde ; tout le reste est facultatif, et les critères fins sont repliés
 * sous « Plus de critères ». Plus d'étapes : chaque clic de plus perd des
 * acheteurs, et l'ancien parcours en quatre écrans en demandait trois.
 *
 * ON MONTRE AVANT DE DEMANDER, comme à /vendre. Dès que le budget et le type
 * sont là, l'aperçu s'affiche AU-DESSUS des coordonnées et suit la saisie :
 * combien de voitures REPÉRÉES CES 14 DERNIERS JOURS correspondent — jamais
 * « disponibles » : une annonce repérée a pu être vendue depuis — et deux ou
 * trois exemples. Si rien ne correspond, PAS D'ÉCRAN D'ÉCHEC : « on cherche
 * pour vous », la demande est enregistrée quand même, et un budget mesuré est
 * suggéré — jamais une voiture hors critères pour remplir l'écran.
 *
 * Les exemples ne portent NI LIEN NI SOURCE. Ce n'est pas une pudeur
 * d'affichage : le lien est ce que ZENKART apporte, et il vient par le
 * recontact. Le serveur ne les envoie même pas (cf. services/recherche.py).
 *
 * Les listes de marques et de modèles — et la correction de fautes qui va avec
 * — sont celles de /vendre : /api/verifier/marques et /api/verifier/modeles.
 * L'API garde ce nom depuis la suppression de la page /verifier (2026-10-15) :
 * une seule liste dans tout le site, sinon « Wolkswagen » serait corrigé sur
 * une page et pas sur l'autre.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import { joli, lieu, nombre, nomVoiture } from '../components/Affaires/format';
import api from '../services/api';
import './TrouverPage.css';

const ENERGIES = ['Essence', 'Diesel', 'Hybride', 'Électrique', 'GPL'];
const BOITES = ['Manuelle', 'Automatique'];
// Repli si /api/recherche/options ne répond pas : le serveur reste la
// référence (il refuse un type qu'il ne connaît pas).
const TYPES_REPLI = [
  { v: 'citadine', l: 'Citadine' }, { v: 'berline', l: 'Berline' },
  { v: 'familiale', l: 'Familiale' }, { v: 'utilitaire', l: 'Utilitaire léger' },
  { v: 'peu_importe', l: 'Peu importe' },
];
const BUDGET_MIN = 500;
// Le temps de finir de taper un budget avant de chercher : en dessous, chaque
// chiffre tapé lance une recherche.
const DELAI_APERCU_MS = 450;

const RECHERCHE_VIDE = {
  budget_max: '', type_voiture: '', marque: '', modele: '',
  km_max: '', annee_min: '', energie: '', boite: '', departement: '',
};
// ⚠️ Le champ leurre ne s'appelle PAS « website », « url », « company » ni
// « address » : ce sont des noms que les navigateurs remplissent tout seuls, et
// un acheteur dont l'autofill remplit le leurre verrait sa demande jetée en
// silence. Nom neutre, aucun attribut `name`, rien que le navigateur puisse
// rattacher à une donnée connue.
const CONTACT_VIDE = { prenom: '', telephone: '', email: '', code_postal: '', piege: '' };

const message = (err, repli) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, '');
  return repli;
};

const entier = (v) => (String(v).trim() === '' ? null : Number(v));

/** La recherche est-elle assez remplie pour être cherchée ? */
export const cherchable = (r) => Number(r.budget_max) >= BUDGET_MIN && Boolean(r.type_voiture);

/** Formulaire -> corps attendu par /api/recherche (listes de marques et de
 *  modèles : le serveur en accepte plusieurs, le formulaire minimal une). */
export function corps(r) {
  const marque = r.marque.trim();
  return {
    marques: marque ? [marque] : [],
    modeles: marque && r.modele.trim() ? [r.modele.trim()] : [],
    budget_max: Number(r.budget_max),
    type_voiture: r.type_voiture,
    annee_min: entier(r.annee_min),
    km_max: entier(r.km_max),
    energie: r.energie || null,
    boite: r.boite || null,
    departement: r.departement || null,
  };
}

/** Niveaux de confiance de la COTE, écrits comme ils sont mesurés.
 *  `affaires.carte` renvoie `fiabilite` (SOLIDE ou CORRECT — les deux seuls
 *  paliers publics, cf. margin_display). On ne passe pas par `AffaireCard`,
 *  qui traduit toute cote en « cote solide » dès qu'un écart existe : ici une
 *  cote CORRECTE doit se dire correcte. */
const CONFIANCE = { SOLIDE: 'cote solide', CORRECT: 'cote correcte' };

/**
 * Une voiture de l'aperçu — DÉLIBÉRÉMENT PAS UN LIEN.
 *
 * `AffaireCard` enveloppe toute la carte dans un `<Link to="/listings/:id">`.
 * L'utiliser ici rendrait la trouvaille cliquable, alors que tout l'intérêt de
 * l'aperçu est de prouver le service SANS le livrer : le serveur ne renvoie
 * déjà ni lien ni source (cf. services/recherche.apercu), ce serait absurde de
 * rouvrir la porte par le front.
 */
export function ApercuCard({ a }) {
  const [photoKo, setPhotoKo] = useState(false);
  const photo = a.photo && !photoKo;
  const ou = lieu(a);
  return (
    <article className="zk-trv-carte">
      <div className={`zk-trv-photo${photo ? '' : ' vide'}`}>
        {photo ? (
          <img src={a.photo} alt="" loading="lazy" referrerPolicy="no-referrer"
            onError={() => setPhotoKo(true)} />
        ) : (
          <span>Photo indisponible</span>
        )}
        {a.ecart_pct != null && <span className="zk-trv-pct">−{Math.round(a.ecart_pct)} %</span>}
      </div>
      <div className="zk-trv-corps">
        <h3>{nomVoiture(a)}</h3>
        <p className="zk-trv-specs">
          {[a.annee, a.km != null ? `${nombre(a.km)} km` : null, a.energie, a.boite]
            .filter(Boolean).join(' · ')}
        </p>
        <p className="zk-trv-prix">{nombre(a.prix)} €</p>
        {a.cote != null && <p className="zk-trv-cote">cote <s>{nombre(a.cote)} €</s></p>}
        {a.ecart_eur != null && a.ecart_eur > 0 && (
          <div className="zk-trv-ecart">
            −{nombre(a.ecart_eur)} €<small>sous la cote</small>
          </div>
        )}
        <p className="zk-trv-confiance">
          {CONFIANCE[a.fiabilite] || 'cote mesurée'}
          {a.comparables ? ` · ${a.comparables} voitures comparées` : ''}
        </p>
        {ou && <p className="zk-trv-ou">{ou}</p>}
      </div>
    </article>
  );
}

/** Le libellé d'un assouplissement, avec la valeur mesurée par le serveur. */
export function libelleElargissement(e) {
  if (e.critere === 'budget') return `${e.verbe} ${nombre(e.valeur)} €`;
  if (e.critere === 'kilométrage') return `${e.verbe} ${nombre(e.valeur)} km`;
  if (e.critere === 'rayon') return `${e.verbe} ${e.valeur} km`;
  return e.verbe;
}

/** Jusqu'à ce nombre, l'aperçu est « presque vide » : on montre ce qu'on a,
 *  mais on dit d'abord qu'on continue de chercher (consigne du 2026-10-15 :
 *  le formulaire doit rester attractif, la demande compte plus que l'aperçu). */
export const PRESQUE_VIDE = 2;

/** « voiture repérée ces 14 derniers jours » — et rien de plus fort. Jamais
 *  « disponible » : une annonce repérée a pu être vendue depuis. */
const reperees = (n, jours) =>
  `voiture${n > 1 ? 's' : ''} repérée${n > 1 ? 's' : ''} ces ${jours} derniers jours`;

/** L'aperçu : il s'affiche AU-DESSUS des coordonnées, avant qu'on les demande.
 *
 *  Pas d'écran d'échec. Quand rien ne correspond, on ne dit pas « rien », on
 *  dit qu'on cherche : la demande est enregistrée quand même, c'est elle qui
 *  compte. Les assouplissements restent proposés, mesurés, en suggestion. */
export function Apercu({ a, onElargir }) {
  const jours = a.jours_fraicheur || 14;
  const elargir = a.elargissements?.length > 0 && (
    <div className="zk-trv-elargir">
      <p className="zk-trv-libelle">Pour voir des voitures tout de suite :</p>
      <ul>
        {a.elargissements.map((e) => (
          <li key={e.critere}>
            <button type="button" className="zk-lien" onClick={() => onElargir(e)}>
              {libelleElargissement(e)}
            </button>
            <span> → {e.total} {reperees(e.total, jours)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  if (a.total === 0) {
    return (
      <section className="zk-trv-apercu attente" aria-label="Ce que nous avons">
        <h2>Pas de correspondance exacte aujourd’hui, mais on cherche pour vous</h2>
        <p className="zk-trv-muet">
          Laissez-nous vos coordonnées ci-dessous : votre demande est enregistrée, nous regardons
          chaque nouvelle annonce et nous vous recontactons dès qu’une voiture correspond.
        </p>
        {elargir}
      </section>
    );
  }

  return (
    <section className="zk-trv-apercu" aria-label="Ce que nous avons">
      <p className="zk-trv-compte">
        <b>{nombre(a.total)}</b> {reperees(a.total, jours)} correspond{a.total > 1 ? 'ent' : ''} à
        votre recherche
      </p>
      {a.total <= PRESQUE_VIDE && (
        <p className="zk-trv-continue">Et on continue de chercher pour vous.</p>
      )}
      <p className="zk-trv-muet">
        Chacune était au moins {a.seuil_pct} % sous la cote des voitures identiques quand nous
        l’avons repérée. Certaines ont pu être vendues depuis : nous vérifions chacune avant de
        vous la proposer.
      </p>
      <div className="zk-trv-exemples">
        {a.exemples.map((annonce) => <ApercuCard key={annonce.id} a={annonce} />)}
      </div>
      <p className="zk-trv-muet">
        {a.total > a.exemples.length
          ? `Voici ${a.exemples.length} exemples sur ${nombre(a.total)}. `
          : ''}
        Nous ne montrons pas les liens ici : c’est précisément le travail que nous faisons pour
        vous. Laissez-nous vos coordonnées, nous vérifions chaque voiture à la main et nous vous
        rappelons avec celles qui tiennent.
      </p>
    </section>
  );
}

/** Le choix du type : des boutons radio, un clic, pas de liste déroulante. */
export function ChoixType({ types, valeur, onChange }) {
  return (
    <fieldset className="zk-trv-types">
      <legend>Type de voiture</legend>
      <div>
        {types.map((t) => (
          <label key={t.v} className={valeur === t.v ? 'on' : undefined}>
            <input type="radio" name="trv-type" value={t.v} required
              checked={valeur === t.v} onChange={() => onChange(t.v)} />
            {t.l}
          </label>
        ))}
      </div>
      <small>Familiale : SUV, break ou monospace.</small>
    </fieldset>
  );
}

export default function TrouverPage() {
  const [recherche, setRecherche] = useState(RECHERCHE_VIDE);
  const [contact, setContact] = useState(CONTACT_VIDE);
  const [consentement, setConsentement] = useState(false);
  const [partenaires, setPartenaires] = useState(false);
  const [apercu, setApercu] = useState(null);
  const [apercuEnCours, setApercuEnCours] = useState(false);
  const [apercuErreur, setApercuErreur] = useState(null);
  const [demande, setDemande] = useState(null);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [correction, setCorrection] = useState('');
  const [options, setOptions] = useState({ types: TYPES_REPLI, departements: [] });
  const [plus, setPlus] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Numéro de la dernière recherche lancée : une réponse plus ancienne qui
  // arrive après une plus récente ne doit pas l'écraser.
  const numero = useRef(0);

  useEffect(() => {
    api.get('/api/verifier/marques').then((r) => setMarques(r.data?.marques || [])).catch(() => {});
    api.get('/api/recherche/options')
      .then((r) => setOptions((o) => ({ ...o, ...r.data, types: r.data?.types?.length ? r.data.types : o.types })))
      .catch(() => {});
  }, []);

  // L'aperçu suit la saisie, dès que budget et type sont là.
  const cle = JSON.stringify(cherchable(recherche) ? corps(recherche) : null);
  useEffect(() => {
    const criteres = JSON.parse(cle);
    const n = ++numero.current;
    if (!criteres) { setApercu(null); setApercuEnCours(false); return undefined; }
    const minuterie = setTimeout(async () => {
      setApercuEnCours(true);
      setApercuErreur(null);
      try {
        const { data } = await api.post('/api/recherche/apercu', criteres);
        if (n === numero.current) setApercu(data);
      } catch (err) {
        if (n === numero.current) {
          setApercuErreur(message(err, 'La recherche n’a pas abouti. Vous pouvez quand même nous laisser votre demande.'));
        }
      } finally {
        if (n === numero.current) setApercuEnCours(false);
      }
    }, DELAI_APERCU_MS);
    return () => clearTimeout(minuterie);
  }, [cle]);

  const maj = (k) => (e) => setRecherche((r) => ({ ...r, [k]: e.target.value }));
  const majContact = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  async function verifierMarque() {
    const saisie = recherche.marque.trim();
    if (saisie.length < 2) { setModeles([]); setCorrection(''); return; }
    try {
      const { data } = await api.get('/api/verifier/modeles', { params: { marque: saisie } });
      setModeles(data?.modeles || []);
      if (data?.marque && data.marque.toLowerCase() !== saisie.toLowerCase()) {
        setRecherche((r) => ({ ...r, marque: data.marque }));
        setCorrection(`« ${saisie} » corrigé en ${data.marque}`);
      } else {
        setCorrection('');
      }
    } catch { /* le serveur corrigera à l'envoi */ }
  }

  /** « Porter le budget à 7 200 € » : on applique, et l'aperçu se relance tout
   *  seul — le chiffre affiché doit être celui qu'on vient de mesurer. */
  function elargir(e) {
    if (e.critere === 'budget') setRecherche((r) => ({ ...r, budget_max: String(e.valeur) }));
    else if (e.critere === 'type') setRecherche((r) => ({ ...r, type_voiture: 'peu_importe' }));
    else if (e.critere === 'kilométrage') setRecherche((r) => ({ ...r, km_max: String(e.valeur) }));
  }

  async function envoyer(e) {
    e.preventDefault();
    setOccupe(true);
    setErreur(null);
    try {
      const { data } = await api.post('/api/recherche/demandes', {
        ...corps(recherche),
        prenom: contact.prenom.trim(),
        telephone: contact.telephone.trim(),
        email: contact.email.trim(),
        code_postal: contact.code_postal.trim(),
        consentement_recontact: consentement,
        consentement_partenaires: partenaires,
        piege: contact.piege,
      });
      setDemande(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErreur(message(err, 'Votre demande n’a pas pu être envoyée. Réessayez dans un moment.'));
    } finally {
      setOccupe(false);
    }
  }

  if (demande) {
    return (
      <Page largeur="etroite" titre="Trouvez ma voiture">
        <section className="zk-carte zk-trv-confirmation">
          <h2>C’est enregistré</h2>
          <p className="zk-trv-montant">Nous cherchons et vous recontactons sous 48 h.</p>
          <p className="zk-trv-muet">
            {/* Aucune promesse de résultat : le service met en relation, il ne
                garantit pas de trouver. Même formulation que les CGU. */}
            Nous ne vous promettons pas de trouver : nous vous promettons de chercher, de vérifier
            chaque voiture à la main, et de vous dire franchement si rien ne tient.
            {demande.id ? ` Votre demande porte le numéro ${demande.id}.` : ''}
          </p>
          {demande.correspondances > 0 && (
            <p className="zk-trv-muet">
              Pour mémoire : {nombre(demande.correspondances)} bonne
              {demande.correspondances > 1 ? 's' : ''} affaire
              {demande.correspondances > 1 ? 's' : ''} correspondai
              {demande.correspondances > 1 ? 'ent' : 't'} à vos critères au moment de votre demande.
            </p>
          )}
        </section>
        <BandeauAffaires titre="En attendant, les bonnes affaires repérées par ZENKART" />
      </Page>
    );
  }

  return (
    <Page
      largeur="etroite"
      titre="Trouvez ma voiture"
      chapeau="Dites-nous ce que vous cherchez. Nous le cherchons pour vous parmi les bonnes affaires que nous repérons."
    >
      <form className="zk-carte zk-trv-form" onSubmit={envoyer}>
        <datalist id="trv-marques">{marques.map((m) => <option key={m} value={m} />)}</datalist>

        <div className="zk-champ">
          <label htmlFor="trv-budget">Budget maximum (€)</label>
          <input id="trv-budget" required type="number" inputMode="numeric" min={BUDGET_MIN}
            max="1000000" step="100" placeholder="12000" value={recherche.budget_max}
            onChange={maj('budget_max')} />
        </div>

        <ChoixType
          types={options.types} valeur={recherche.type_voiture}
          onChange={(v) => setRecherche((r) => ({ ...r, type_voiture: v }))}
        />

        <div className="zk-trv-grille">
          <div className="zk-champ">
            <label htmlFor="trv-marque">Marque <em>facultatif</em></label>
            <input id="trv-marque" list="trv-marques" autoComplete="off" maxLength={60}
              placeholder="Peugeot" value={recherche.marque} onChange={maj('marque')}
              onBlur={verifierMarque} />
            {correction && <small className="zk-trv-corrige">{correction}</small>}
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-modele">Modèle <em>facultatif</em></label>
            <input id="trv-modele" list="trv-modeles" autoComplete="off" maxLength={60}
              placeholder="208" value={recherche.modele} onChange={maj('modele')}
              disabled={!recherche.marque.trim()} />
            <datalist id="trv-modeles">
              {modeles.map((m) => <option key={m} value={joli(m)} />)}
            </datalist>
          </div>
        </div>

        <div className="zk-champ">
          <label htmlFor="trv-dep">Département</label>
          <select id="trv-dep" value={recherche.departement} onChange={maj('departement')}>
            <option value="">Toute la France</option>
            {(options.departements || []).map((d) => (
              <option key={d.v} value={d.v}>{d.l}</option>
            ))}
          </select>
        </div>

        <button type="button" className="zk-lien zk-trv-plus" aria-expanded={plus}
          aria-controls="trv-plus" onClick={() => setPlus((p) => !p)}>
          {plus ? '− Moins de critères' : '+ Plus de critères'}
        </button>
        <div id="trv-plus" className="zk-trv-grille" hidden={!plus}>
          <div className="zk-champ">
            <label htmlFor="trv-km">Kilométrage maximum</label>
            <input id="trv-km" type="number" inputMode="numeric" min="0" step="1000"
              placeholder="120000" value={recherche.km_max} onChange={maj('km_max')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-annee">Année minimum</label>
            <input id="trv-annee" type="number" inputMode="numeric" min="1950" max="2100"
              placeholder="2016" value={recherche.annee_min} onChange={maj('annee_min')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-energie">Énergie</label>
            <select id="trv-energie" value={recherche.energie} onChange={maj('energie')}>
              <option value="">Peu importe</option>
              {ENERGIES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-boite">Boîte</label>
            <select id="trv-boite" value={recherche.boite} onChange={maj('boite')}>
              <option value="">Peu importe</option>
              {BOITES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>

        {/* L'aperçu, AVANT les coordonnées. `aria-live` : un lecteur d'écran
            entend le nombre changer quand la recherche change. */}
        <div className="zk-trv-resultat" aria-live="polite" aria-busy={apercuEnCours}>
          {!cherchable(recherche) && (
            <p className="zk-trv-attente">
              Indiquez votre budget et le type de voiture : nous vous montrons tout de suite ce que
              nous avons.
            </p>
          )}
          {cherchable(recherche) && !apercu && !apercuErreur && (
            <p className="zk-trv-attente">Recherche…</p>
          )}
          {cherchable(recherche) && apercuErreur && (
            <p className="zk-trv-attente">{apercuErreur}</p>
          )}
          {cherchable(recherche) && apercu && (
            <div className={apercuEnCours ? 'zk-trv-rafraichit' : undefined}>
              <Apercu a={apercu} onElargir={elargir} />
            </div>
          )}
        </div>

        <h2 className="zk-trv-section">Où vous joindre ?</h2>
        <div className="zk-trv-grille">
          <div className="zk-champ">
            <label htmlFor="trv-prenom">Prénom</label>
            <input id="trv-prenom" required maxLength={60} autoComplete="given-name"
              value={contact.prenom} onChange={majContact('prenom')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-tel">Téléphone</label>
            <input id="trv-tel" required type="tel" autoComplete="tel"
              placeholder="06 12 34 56 78" value={contact.telephone}
              onChange={majContact('telephone')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-email">Email</label>
            <input id="trv-email" required type="email" autoComplete="email"
              value={contact.email} onChange={majContact('email')} />
          </div>
          <div className="zk-champ">
            <label htmlFor="trv-cp">Code postal</label>
            <input id="trv-cp" required inputMode="numeric" pattern="\d{5}" maxLength={5}
              placeholder="33000" autoComplete="postal-code" value={contact.code_postal}
              onChange={majContact('code_postal')} />
          </div>
        </div>

        {/* Honeypot : invisible, un humain ne le remplit jamais. */}
        <input type="text" className="zk-trv-piege" tabIndex={-1} autoComplete="off"
          aria-hidden="true" value={contact.piege} onChange={majContact('piege')} />

        <label className="zk-trv-consentement">
          <input type="checkbox" checked={consentement} required
            onChange={(e) => setConsentement(e.target.checked)} />
          <span>
            J’accepte que <b>ZENKART me recontacte</b> au sujet de cette recherche, par téléphone
            ou par email.
          </span>
        </label>
        <label className="zk-trv-consentement">
          <input type="checkbox" checked={partenaires}
            onChange={(e) => setPartenaires(e.target.checked)} />
          <span>
            J’accepte que ma demande soit transmise, <b>sous forme anonyme</b> (critères, budget
            et secteur, sans mes coordonnées), à des professionnels partenaires susceptibles
            d’avoir le véhicule. <em>Facultatif.</em>
          </span>
        </label>

        {erreur && <div role="alert" className="zk-alerte erreur zk-trv-bloc">{erreur}</div>}

        <button type="submit" className="zk-bouton plein" disabled={occupe || !consentement}>
          {occupe ? 'Envoi…' : 'Qu’on me trouve cette voiture'}
        </button>
        <p className="zk-trv-muet">
          Gratuit, sans compte. Nous cherchons à la main et nous vous rappelons sous 48 h. Vos
          coordonnées servent uniquement à cette recherche ; elles sont conservées 12 mois après
          notre dernier échange, puis supprimées. Pour les faire supprimer plus tôt :{' '}
          <a href="mailto:privacy@zenkart.fr" className="zk-lien">privacy@zenkart.fr</a>.{' '}
          <Link to="/confidentialite" className="zk-lien">Politique de confidentialité</Link>.
        </p>
      </form>

      <BandeauAffaires titre="En attendant, les bonnes affaires repérées par ZENKART" />
    </Page>
  );
}
